import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import Lenis from "lenis";

import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Insights from "@/pages/Insights";
import Article from "@/pages/Article";
import Apply from "@/pages/Apply";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Cookies from "@/pages/Cookies";
import CookieConsent from "@/components/site/CookieConsent";
import BookCheckup from "@/pages/BookCheckup";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

let lenisInstance = null;

const useLenis = () => {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true, wheelMultiplier: 0.9 });
    lenisInstance = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisInstance = null;
    };
  }, []);
};

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
        return;
      }
    }
    window.scrollTo(0, 0);
    if (lenisInstance) lenisInstance.scrollTo(0, { immediate: true });
  }, [pathname, hash]);
  return null;
};

const Shell = () => {
  useLenis();
  return (
    <div className="App grain min-h-screen bg-[#0E1526]">
      <ScrollManager />
      <Nav />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<Article />} />
          <Route path="/apply" element={<Apply />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/symptom-checker" element={<Navigate to="/" replace />} />
          <Route path="/book-checkup" element={<BookCheckup />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Shell />
        <CookieConsent />
        <a
          href="https://wa.me/33770241597?text=Hi!%20I'd%20like%20to%20talk%20about%20my%20business%20check-up."
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          data-testid="whatsapp-fab"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg shadow-[#25D366]/30 hover:scale-110 active:scale-95 transition-transform duration-300"
        >
          <WhatsAppIcon size={28} />
        </a>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
